import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Trash2, FileUp, ShoppingCart } from 'lucide-react';

// STL File List Sidebar Component
function STLFileList({ files, currentIndex, onSelectFile, onRemoveFile, onQuantityChange, onAddAllToCart, isSlicing = false, isQuoteStale = false, isCalculating = false }) {
  if (files.length === 0) {
    return null;
  }

  const filesWithQuote = files.filter(f => f.quote);
  const totalPrice = filesWithQuote
    .reduce((sum, f) => sum + (f.quote?.price || 0) * (f.quantity || 1), 0);

  return (
    <Card className="bg-white text-gray-900">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-sm text-gray-900">Hochgeladene STL-Dateien</h3>
          <span className="text-xs text-gray-600">{files.length} Datei{files.length !== 1 ? 'en' : ''}</span>
        </div>

        <div className="space-y-2">
          {files.map((file, index) => (
            <div
              key={file.analysis.id || file.analysis.filename}
              className={`p-3 rounded-lg border-2 transition-all ${
                index === currentIndex
                  ? 'border-red-700 bg-red-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              {/* Dateiname + Löschen-Button – klickbar für Auswahl */}
              <div
                className="flex items-start justify-between cursor-pointer"
                onClick={() => onSelectFile(index)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <FileUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <p className="text-sm font-medium truncate text-gray-900" title={file.analysis.filename}>
                      {file.analysis.filename}
                    </p>
                    {file.quote
                      ? <span className="flex-shrink-0 text-xs font-semibold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full leading-none" title="Preis berechnet">✓</span>
                      : <span className="flex-shrink-0 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full leading-none" title="Kein Preis berechnet">–</span>
                    }
                  </div>

                  {file.material && file.color && (
                    <p className="text-xs text-gray-600 mt-1">
                      {file.material} • {file.color}
                    </p>
                  )}
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 flex-shrink-0 ml-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveFile(index);
                  }}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>

              {/* Stückzahl + Preis */}
              <div className="mt-2 flex items-center justify-between gap-2">
                {/* Stückzahl-Steuerung */}
                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => onQuantityChange(index, (file.quantity || 1) - 1)}
                    disabled={(file.quantity || 1) <= 1}
                    className="w-6 h-6 rounded border border-gray-300 flex items-center justify-center text-sm font-bold hover:bg-gray-100 disabled:opacity-40 leading-none"
                  >−</button>
                  <input
                    type="number"
                    min="1"
                    max="999"
                    value={file.quantity || 1}
                    onChange={e => onQuantityChange(index, e.target.value)}
                    className="w-12 text-center text-sm font-semibold border border-gray-300 rounded py-0.5 focus:outline-none focus:ring-1 focus:ring-red-600"
                  />
                  <button
                    onClick={() => onQuantityChange(index, (file.quantity || 1) + 1)}
                    disabled={(file.quantity || 1) >= 999}
                    className="w-6 h-6 rounded border border-gray-300 flex items-center justify-center text-sm font-bold hover:bg-gray-100 disabled:opacity-40 leading-none"
                  >+</button>
                  <span className="text-xs text-gray-500">Stk.</span>
                </div>

                {/* Preis dieses Eintrags */}
                {file.quote ? (
                  <div className="text-right">
                    {(file.quantity || 1) > 1 && (
                      <p className="text-xs text-gray-400 leading-none">{file.quote.price.toFixed(2)}€ /Stk.</p>
                    )}
                    <p className="text-sm font-semibold text-red-700 leading-tight">
                      {(file.quote.price * (file.quantity || 1)).toFixed(2)}€
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">kein Preis</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Gesamtpreis aller Dateien */}
        {filesWithQuote.length > 0 && (
          <>
            {filesWithQuote.length > 1 && (
              <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-700">Gesamt:</span>
                <span className="text-base font-bold text-red-700">€{totalPrice.toFixed(2)}</span>
              </div>
            )}
            <button
              onClick={onAddAllToCart}
              disabled={isSlicing || isCalculating || isQuoteStale}
              title={isSlicing ? 'Slicer läuft – bitte warten' : (isCalculating || isQuoteStale) ? 'Preisberechnung läuft – bitte warten' : undefined}
              className={`mt-3 w-full font-bold py-3 px-4 rounded-lg shadow-md transition-colors duration-200 flex items-center justify-center gap-2 text-sm ${
                isSlicing || isCalculating || isQuoteStale
                  ? 'bg-gray-400 cursor-not-allowed text-white opacity-60'
                  : 'bg-red-700 hover:bg-red-800 text-white'
              }`}
            >
              <ShoppingCart className="w-4 h-4" />
              {isSlicing
                ? 'Slicer läuft…'
                : (isCalculating || isQuoteStale)
                ? 'Preisberechnung läuft…'
                : <>Alle {filesWithQuote.length} Datei(en) in den Warenkorb{filesWithQuote.length > 0 && <span className="ml-1">(€{totalPrice.toFixed(2)})</span>}</>
              }
            </button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default STLFileList;
